import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getMailcomInventory,
  getMailcomReadiness,
  getMailcomSummary,
  listMailcomAccounts,
  listMailcomEvents,
  type WireProviderAccount,
} from '../../api/endpoints.js';
import { useApiQuery } from '../../api/hooks.js';
import { useCan } from '../../session/can.js';
import { Banner, ErrorState, Icon, PageHead, Tabs } from '../../ui/index.js';
import { AccountsGrid } from './AccountsGrid.js';
import { AliasListModal } from './AliasListModal.js';
import { BulkImportModal } from './BulkImportModal.js';
import { EventsPanel } from './EventsPanel.js';
import { InboxOtpPanel, InventoryPanel } from './InventoryPanel.js';
import { AliasLifecycleStats, MailcomSummary, OAuthConfigPanel } from './MailcomSummary.js';
import { MailcomToolbar } from './MailcomToolbar.js';
import { DrainingPanelConnected, SupplyCardsConnected } from './MailcomSupply.js';
import { RenewalHistoryModal } from './RenewalHistoryModal.js';
import { UpdateCredentialModal } from './UpdateCredentialModal.js';
import '../../styles/mailcom.mobile.css';

// R-D214 — V1-parity status taxonomy. Mirror of the AccountsGrid union
// because MailcomSummary also drives this state (Stat card clicks).
type StatusFilter =
  | 'all'
  | 'active'
  | 'draining'
  | 'suspended'
  | 'login_failed'
  | 'disabled'
  | 'alias_limit_reached'
  | 'renewal_failed'
  | 'renewal_in_progress'
  | 'upstream_unavailable'
  | 'blocked'
  | 'bootstrapping'
  | 'phone_verification_required'
  // 2026-09-23 (D-239) — status_detail filter tokens.
  | 'operator_off_sale'
  | 'imap_rejected';
type TabId = 'parents' | 'inventory' | 'events' | 'readiness' | 'draining';

const ACCOUNTS_PAGE_SIZE = 50;

// 2026-06-06 — live summary auto-refresh interval. Operator constraint: ONLY
// the lightweight projection-based summary cards refresh on this timer; the
// heavy parent-mail list (accountsQuery) is NEVER auto-refetched here.
const SUMMARY_POLL_MS = 10_000;

/**
 * Lightweight overlap-safe poller for the summary/stat cards ONLY.
 *
 * Constraints (operator, "must not strain the system"):
 *  - reuses the EXISTING summary fetch (`reload`) — no new endpoint, no full
 *    list refetch;
 *  - overlap-safe: never starts a new request while the previous summary
 *    request is still in flight (`isLoading` gate). A skipped tick simply waits
 *    for the next interval — no queueing, no abort-restart churn;
 *  - clears the timer on unmount;
 *  - pauses while the tab is hidden, and fires one immediate catch-up reload
 *    when the tab becomes visible again.
 *
 * `reload`/`busy` are funnelled through refs so the interval is created
 * exactly once and is not torn down on every summary state transition.
 *
 * 2026-06-09 flicker fix: `busy` is now the SWR `refreshing` flag (the poll
 * keeps showing prior data and never flips the panel to a blanking `loading`
 * state). The overlap guard is unchanged in spirit — skip a tick while a
 * background revalidation is still in flight.
 */
function useSummaryPoll(reload: () => void, busy: boolean, intervalMs: number): void {
  const reloadRef = useRef(reload);
  const isLoadingRef = useRef(busy);
  reloadRef.current = reload;
  isLoadingRef.current = busy;

  useEffect(() => {
    const tick = (): void => {
      // Skip while the tab is hidden, or while a summary request is still
      // pending (overlap guard). Both keep the system load minimal.
      if (typeof document !== 'undefined' && document.hidden) return;
      if (isLoadingRef.current) return;
      reloadRef.current();
    };
    const id = window.setInterval(tick, intervalMs);
    const onVisibility = (): void => {
      if (!document.hidden && !isLoadingRef.current) reloadRef.current();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs]);
}

/**
 * mail.com Parent Mailler — orchestrator.
 *
 * R-UI-MAILCOM-V1-ADAPT (D-155 candidate, 2026-05-27): re-organized into 4
 * tabs (Parent Mailler / Envanter / Outbox / Hazırlık) with a V1-style
 * dense table on the Parent tab. ParentMailCard is no longer referenced
 * here — AccountsGrid renders ParentMailsTable instead.
 *
 * All API contracts, capability gates and modal flows are preserved verbatim;
 * only presentation changed.
 */
export interface MailcomPageProps {
  /**
   * R-RAMBLER (2026-09-15 audit) — `false` hides the alias-model mutations
   * (bulk-renew, revive, retry-failed, force-renew, export-and-delete,
   * bulk-delete-and-renew) for a provider whose unit IS the mailbox; the API
   * answers 409 for them anyway, so the buttons would only mislead. Default
   * `true` keeps every existing mount byte-identical.
   */
  readonly aliasOps?: boolean;
  /**
   * Which provider this page instance is bound to. Default 'mailcom' keeps every
   * existing mount (mailcom-control-plane, providers/mailcom) byte-behaviour. The
   * GMX mount passes 'gmx'; the SAME components then call /admin/providers/gmx/*
   * via the providerId-threaded endpoints. Provider isolation is enforced HERE:
   * the value is fixed per route and flows down to every endpoint closure, so a
   * page can never read or write a provider other than the one it was mounted
   * with.
   */
  readonly providerId?: string;
  /** Human label shown in crumb/title/subtitle (e.g. 'mail.com' or 'gmx.com'). */
  readonly providerLabel?: string;
  /**
   * D-246 — the provider line may be PASSIVE (the GMX alias line: sales and
   * renewal switched off, code and history kept). When set, the page is
   * READ-ONLY until the server's summary explicitly says it is NOT passive
   * (fail-safe: while the summary loads, or if it cannot be read, no action is
   * offered), and shows a "pasif" banner once the server confirms `passive`.
   * Every action (alias ops, import, credential edit/reveal, off-sale,
   * export-and-delete) is hidden; the server rejects them too (409
   * provider_passive), so this is presentation, not the safety boundary.
   * Default `false` keeps every other mount byte-identical.
   */
  readonly passiveAware?: boolean;
}

export function MailcomPage({
  providerId = 'mailcom',
  providerLabel = 'mail.com',
  aliasOps: aliasOpsProp = true,
  passiveAware = false,
}: MailcomPageProps = {}): JSX.Element {
  const canView = useCan('provider:inspect');
  const canManageCap = useCan('provider:manage_inventory');
  const canDrainCap = useCan('provider:drain');

  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsSearch, setAccountsSearch] = useState('');
  const [accountsStatus, setAccountsStatus] = useState<StatusFilter>('all');
  const [activeTab, setActiveTab] = useState<TabId>('parents');

  const [showImport, setShowImport] = useState(false);
  const [aliasModalFor, setAliasModalFor] = useState<string | null>(null);
  const [renewalModalFor, setRenewalModalFor] = useState<string | null>(null);
  const [credentialModalFor, setCredentialModalFor] = useState<string | null>(null);

  const summaryQuery = useApiQuery((o) => getMailcomSummary(o, providerId), [providerId]);
  // D-246 — server-driven passivity (see MailcomPageProps.passiveAware). A
  // passive-aware page is read-only unless the summary has been read AND says
  // the line is live; the banner needs the server's explicit `passive: true`.
  const serverPassive = summaryQuery.kind === 'ok' && summaryQuery.data?.passive === true;
  const serverLive = summaryQuery.kind === 'ok' && summaryQuery.data?.passive !== true;
  const readOnly = passiveAware && !serverLive;
  const canManage = canManageCap && !readOnly;
  const canDrain = canDrainCap && !readOnly;
  const aliasOps = aliasOpsProp && !readOnly;
  const readinessQuery = useApiQuery((o) => getMailcomReadiness(o, providerId), [providerId]);
  const accountsQuery = useApiQuery(
    (o) =>
      listMailcomAccounts(o, {
        page: accountsPage,
        limit: ACCOUNTS_PAGE_SIZE,
        search: accountsSearch,
        // 2026-09-23 (D-239) — the take-off-sale / IMAP-quarantine tokens are
        // status_detail values; the client's `status` union does not carry
        // them, so they ride the explicit statusDetail filter (same wire).
        ...(accountsStatus === 'operator_off_sale' || accountsStatus === 'imap_rejected'
          ? { statusDetail: accountsStatus }
          : { status: accountsStatus }),
      }, providerId),
    [accountsPage, accountsSearch, accountsStatus, providerId],
  );
  const inventoryQuery = useApiQuery((o) => getMailcomInventory(o, providerId), [providerId]);
  const eventsQuery = useApiQuery((o) => listMailcomEvents(o, 50, providerId), [providerId]);

  // Live auto-refresh of the stat cards. Both the projection summary AND the
  // current accounts page reload on each tick — the stat cards derive their
  // numbers primarily from accountsQuery.statusBreakdown/runningRenewals (the
  // fresh-count source), so refreshing only the summary would leave the cards
  // silently stale (no-silent-stale rule). Stale-while-revalidate (the hook
  // keeps prior data + raises `refreshing`) means neither reload blanks the
  // panel — the numbers update in place, no remount, no flicker. Overlap-safe
  // (skip while either revalidation is in flight) + tab-hidden-aware.
  const pollReload = useCallback(() => {
    summaryQuery.reload();
    accountsQuery.reload();
  }, [summaryQuery, accountsQuery]);
  useSummaryPoll(
    pollReload,
    summaryQuery.refreshing || accountsQuery.refreshing,
    SUMMARY_POLL_MS,
  );

  const accounts = accountsQuery.kind === 'ok' ? accountsQuery.data?.accounts ?? [] : [];

  const credentialAccount = useMemo<WireProviderAccount | null>(
    () => accounts.find((a) => a.id === credentialModalFor) ?? null,
    [accounts, credentialModalFor],
  );

  const refreshAll = useCallback(() => {
    summaryQuery.reload();
    readinessQuery.reload();
    accountsQuery.reload();
    inventoryQuery.reload();
    eventsQuery.reload();
  }, [accountsQuery, eventsQuery, inventoryQuery, readinessQuery, summaryQuery]);

  const refreshAfterAccountChange = useCallback(() => {
    accountsQuery.reload();
    summaryQuery.reload();
    readinessQuery.reload();
    inventoryQuery.reload();
  }, [accountsQuery, inventoryQuery, readinessQuery, summaryQuery]);

  if (!canView) {
    return (
      <div className="mcp-page">
        <PageHead
          crumb={<>Yönetim <Icon.ChevronRight size={11} /> {providerLabel} Parent Mailler</>}
          title={`${providerLabel} Parent Mailler`}
          subtitle="Bu sayfayı görmek için provider:inspect yetkisi gerekiyor."
        />
        <ErrorState reason="capability_missing:provider:inspect" />
      </div>
    );
  }

  return (
    <div className="mcp-page" data-testid={`${providerId}-parent-mails-page`}>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> {providerLabel} Parent Mailler</>}
        title={`${providerLabel} Parent Mailler`}
        subtitle={
          <>
            {providerLabel} parent hesapları, alias stokları, session / renewal durumu ve inbox / OTP akışı buradan
            yönetilir. Tüm kayıtlar Postgres <code>provider_accounts</code> / <code>provider_aliases</code> /{' '}
            <code>provider_session_blobs</code> / <code>provider_renewal_jobs</code> tablolarında, değişiklikler
            transactional outbox üzerinden işlenir. Ham şifre, refresh token, cookie veya session id bu yüzeyde
            gözükmez.
          </>
        }
      />

      {passiveAware && serverPassive ? (
        <Banner
          tone="warn"
          title={`${providerLabel} alias hattı pasif`}
          testId={`${providerId}-passive-banner`}
        >
          Satış ve otomatik yenileme durduruldu; sayfa salt okunurdur. Geçmiş ve stok görüntülenebilir, hiçbir işlem
          (alias ekleme, yenileme, import, kimlik bilgisi, dışa aktarma) yapılamaz.
        </Banner>
      ) : null}

      <Tabs
        tabs={[
          { id: 'parents', label: 'Parent Mailler', testId: 'tab-parents' },
          { id: 'inventory', label: 'Envanter', testId: 'tab-inventory' },
          { id: 'events', label: 'Outbox', testId: 'tab-events' },
          { id: 'readiness', label: 'Hazırlık', testId: 'tab-readiness' },
          // WS5b (Q67) — mail.com only: every draining parent, why and since when.
          ...(providerId === 'mailcom'
            ? [{ id: 'draining', label: 'Draining', testId: 'tab-draining' }]
            : []),
        ]}
        activeId={activeTab}
        onSelect={(id) => setActiveTab(id as TabId)}
        testId="mailcom-tabs"
      />

      {activeTab === 'parents' ? (
        <div className="mcp-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="tab-panel-parents">
          <MailcomToolbar
            providerId={providerId}
            canManage={canManage}
            canDrain={canDrain}
            onRefreshAll={refreshAll}
            onOpenImport={() => setShowImport(true)}
            activeStatusFilter={accountsStatus}
            aliasOps={aliasOps}
            readOnly={readOnly}
          />

          <MailcomSummary
            summaryQuery={summaryQuery}
            accountsQuery={accountsQuery}
            readinessQuery={readinessQuery}
            onRefresh={refreshAll}
            providerLabel={providerLabel}
            providerId={providerId}
            activeStatusFilter={accountsStatus}
            onSelectStatus={(s) => {
              setAccountsPage(1);
              setAccountsStatus(s);
            }}
          />

          {/* WS5b (Q64/Q66) — mail.com lifetime-cap supply view. */}
          {providerId === 'mailcom' ? (
            <SupplyCardsConnected onOpenDraining={() => setActiveTab('draining')} />
          ) : null}

          <AccountsGrid
            providerId={providerId}
            query={accountsQuery}
            canManage={canManage}
            onOpenAliases={setAliasModalFor}
            onOpenRenewals={setRenewalModalFor}
            onOpenCredential={setCredentialModalFor}
            onAfterAction={refreshAfterAccountChange}
            page={accountsPage}
            onPageChange={setAccountsPage}
            search={accountsSearch}
            onSearchChange={(s) => {
              setAccountsPage(1);
              setAccountsSearch(s);
            }}
            statusFilter={accountsStatus}
            onStatusFilterChange={(s) => {
              setAccountsPage(1);
              setAccountsStatus(s);
            }}
            pageSize={ACCOUNTS_PAGE_SIZE}
          />
        </div>
      ) : null}

      {activeTab === 'inventory' ? (
        <div className="mcp-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="tab-panel-inventory">
          <AliasLifecycleStats summaryQuery={summaryQuery} />
          <InventoryPanel query={inventoryQuery} onRefresh={inventoryQuery.reload} />
        </div>
      ) : null}

      {activeTab === 'events' ? (
        <div data-testid="tab-panel-events">
          <EventsPanel query={eventsQuery} onRefresh={eventsQuery.reload} />
        </div>
      ) : null}

      {activeTab === 'draining' && providerId === 'mailcom' ? (
        <div data-testid="tab-panel-draining">
          <DrainingPanelConnected />
        </div>
      ) : null}

      {activeTab === 'readiness' ? (
        <div className="mcp-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="tab-panel-readiness">
          {/* OAuth config is mail.com-specific (OAuth2 quath). Other providers
              (e.g. gmx.com, authorized IMAP) have no equivalent env surface, so
              showing the mail.com env table on their page is a provider-leak. */}
          {providerId === 'mailcom' ? (
            <OAuthConfigPanel query={readinessQuery} onRefresh={readinessQuery.reload} />
          ) : null}
          <InboxOtpPanel />
        </div>
      ) : null}

      <AliasListModal
        providerId={providerId}
        open={aliasModalFor !== null}
        accountId={aliasModalFor}
        canManage={canManage}
        onClose={() => setAliasModalFor(null)}
      />
      <RenewalHistoryModal
        providerId={providerId}
        open={renewalModalFor !== null}
        accountId={renewalModalFor}
        canManage={canManage}
        onClose={() => setRenewalModalFor(null)}
      />
      <UpdateCredentialModal
        providerId={providerId}
        open={!readOnly && credentialModalFor !== null}
        account={credentialAccount}
        onClose={() => setCredentialModalFor(null)}
        onSuccess={refreshAfterAccountChange}
      />
      {canManage ? (
        <BulkImportModal
          providerId={providerId}
          providerLabel={providerLabel}
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={refreshAfterAccountChange}
        />
      ) : null}
    </div>
  );
}
